/**
 * In-game HUD, vanilla layout in GUI pixels (inside a `GuiStage` from kit.ts).
 *
 *   const hud = new GameHud(stage, icons, inventory);   // stage.interactive = false is set here
 *   hud.update({...});     // every frame (cheap: DOM writes only on change)
 *   hud.refreshHotbar();   // optional — update() also notices inventory edits itself
 *
 * Coordinates are vanilla's (Gui.java, 1.21): hotbar (W/2-91, H-22), hearts (W/2-91, H-39),
 * food right-aligned to W/2+91, armor one row (10px) above the hearts, air above the food,
 * xp bar (W/2-91, H-29) with the level number at H-35, crosshair 15x15 centred, attack
 * indicator at (W/2-8, H/2+9), held-item name at H-59 (+14 in creative), action bar at H-68.
 * In creative the hearts/food/armor/xp are hidden.
 *
 * Heart behaviour follows vanilla: <= 4 health shakes each heart (random 0..1 px per tick),
 * taking damage flashes the *lost* hearts with the `_blinking` textures for 20 ticks.
 */

import { mcText, textWidth } from './kit';
import { ItemIcons } from './itemIcons';
import { itemDef } from '../game/itemdb';
import { stackName } from '../items';
import { HOTBAR, type Inventory } from '../game/inventory';
import { isEmpty } from '../game/types';
import { I } from '../game/itemIds';
import type { GuiStage } from './kit';
import { type HeartStyle, heartSprite, foodSprite, foodEmptySprite, regenHeart } from './heartStyle';
import { COMBAT_EDITION } from '../game/combat';

/** One player the locator bar can draw, in camera-relative polar coordinates. */
export interface LocatorWaypoint {
  /** Peer id — vanilla tints a waypoint from the player's UUID, so this picks the tint. */
  id: string;
  /** Bearing in degrees, positive to the right of the crosshair. */
  bearing: number;
  /** Distance in blocks; drives which of the four dot sizes shows. */
  dist: number;
  /** Target eye level in degrees above (+) or below (-) the crosshair. */
  elev: number;
}

export interface HudState {
  health: number; maxHealth: number; food: number; saturation: number; armor: number;
  air: number; maxAir: number; xpLevel: number; xpProgress: number; creative: boolean;
  /** 0..1 attack cooldown (1 = ready). */
  attackCooldown: number;
  /** Integer game tick counter (20 Hz). */
  tick: number;
  /** 0..1 red vignette strength (decays in the caller). */
  hurtFlash: number;
  lowHealth: boolean;
  /**
   * Vanilla locator bar (1.21.6): in a session these waypoints replace the XP
   * bar, so this is null in single player, with nobody else around, and for the
   * 100 ticks after the XP bar was updated.
   */
  locator?: LocatorWaypoint[] | null;
  /** Status-effect looks: heart tint, hunger tint, regeneration wave. */
  heartStyle?: HeartStyle;
  hungry?: boolean;
  regen?: boolean;
  /** True when the held item has its own cooldown bar (spear jab); Bedrock otherwise shows none. */
  showCooldownBar?: boolean;
}

const HUD_SPR = 'mc/gui/sprites/hud/';
const urlOf = (n: string): string => `url(${HUD_SPR}${n}.png)`;
const PIX = 'image-rendering:pixelated;pointer-events:none;';

/** Vanilla `waypoint_style/default`: four dot sprites spread over 128..332 blocks. */
const LOCATOR_NEAR = 128, LOCATOR_FAR = 332, LOCATOR_STEPS = 4;
/** Only waypoints inside 120 degrees of the camera are drawn (vanilla). */
const LOCATOR_ARC = 60;
/** Dot pool ceiling; a 10-player room never gets near it. */
const LOCATOR_MAX = 24;
/** ChatFormatting colours, which vanilla assigns waypoints from (black dropped: the bar is dark). */
const LOCATOR_COLORS = [
  '#0000aa', '#00aa00', '#00aaaa', '#aa0000', '#aa00aa', '#ffaa00', '#aaaaaa', '#555555',
  '#5555ff', '#55ff55', '#55ffff', '#ff5555', '#ff55ff', '#ffff55', '#ffffff',
];

/** Stable tint per player: vanilla hashes the UUID, we hash the peer id the same way. */
function locatorColor(id: string): string {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) { h ^= id.charCodeAt(i); h = Math.imul(h, 16777619); }
  return LOCATOR_COLORS[(h >>> 0) % LOCATOR_COLORS.length];
}

/**
 * Paint a GUI sprite in a player's colour the way vanilla blits it: a plain
 * multiply, so the black outline stays black and the white core takes the tint.
 * CSS needs the mask (multiply alone would leave the colour in the transparent
 * padding) and an explicit sprite size (`w` x `h` is the window into the sheet:
 * the arrow sprites hold two 7x5 frames, of which we show the first).
 */
function tintSprite(e: HTMLElement, sprite: string, color: string, w: number, h: number): void {
  const url = urlOf(sprite);
  e.style.backgroundImage = url;
  e.style.backgroundColor = color;
  e.style.backgroundSize = `${w}px ${h}px`;
  e.style.backgroundRepeat = 'no-repeat';
  e.style.backgroundBlendMode = 'multiply';
  const mask = `${url} 0 0 / ${w}px ${h}px no-repeat`;
  e.style.setProperty('-webkit-mask', mask);
  e.style.setProperty('mask', mask);
}

interface LocatorDot {
  readonly dot: HTMLDivElement;
  readonly arrow: HTMLDivElement;
  dotSprite: string;
  arrowSprite: string;
  color: string;
}

/** Absolutely-positioned sprite div (background swapped via `set`). */
class Spr {
  readonly el = document.createElement('div');
  private cur = '';
  constructor(name: string, x: number, y: number, w: number, h: number) {
    this.el.style.cssText = `position:absolute;width:${w}px;height:${h}px;background-size:100% 100%;background-repeat:no-repeat;${PIX}`;
    this.pos(x, y);
    this.set(name);
  }
  pos(x: number, y: number): void { this.el.style.left = x + 'px'; this.el.style.top = y + 'px'; }
  set(name: string): void {
    if (name === this.cur) return;
    this.cur = name;
    this.el.style.backgroundImage = name ? urlOf(name) : 'none';
  }
}

/** Cheap deterministic per-(tick, i) hash in 0..1 — vanilla reseeds Random(tick*312871). */
function rnd01(tick: number, i: number): number {
  let h = (Math.imul(tick | 0, 312871) ^ Math.imul(i + 1, 0x9e3779b1)) >>> 0;
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b) >>> 0;
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35) >>> 0;
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

export class GameHud {
  private readonly root = document.createElement('div');
  private W = 0;
  private H = 0;

  // hotbar
  private readonly hotbarBg: Spr;
  private readonly selection: Spr;
  private readonly offhandBg: Spr;
  private readonly slotEls: HTMLDivElement[] = [];
  private readonly offhandEl = document.createElement('div');
  private invSig = '';
  private lastSel = -1;
  private lastHeldKey = '';

  // status bars
  private readonly heartBg: Spr[] = [];
  private readonly heartFg: Spr[] = [];
  private readonly heartBlink: Spr[] = [];
  private readonly foodBg: Spr[] = [];
  private readonly foodFg: Spr[] = [];
  private readonly armorSp: Spr[] = [];
  private readonly airSp: Spr[] = [];
  private readonly xpBg: Spr;
  private readonly xpFg: Spr;
  private xpLevelEl: HTMLDivElement | null = null;
  private xpLevelShown = -1;

  // locator bar (vanilla 1.21.6): stands in for the XP bar in a session
  private readonly locatorBg = document.createElement('div');
  private readonly locatorDots: LocatorDot[] = [];

  // crosshair
  private readonly crosshair: Spr;
  private readonly atkBg: Spr;
  private readonly atkFg: Spr;

  // overlays + text
  private readonly waterEl = document.createElement('div');
  private readonly vignetteEl = document.createElement('div');
  private readonly heldNameEl = document.createElement('div');
  private readonly toastEl = document.createElement('div');
  private readonly coordsEl = document.createElement('div');
  private readonly debugEl = document.createElement('div');
  private heldNameUntil = 0;
  private toastUntil = 0;
  private coordsText = '';
  private debugText = '';
  private debugOn = false;

  // heart history for the damage blink
  private lastHealth = -1;
  private displayHealth = 0;
  private blinkUntilTick = 0;

  private visible = true;

  constructor(private readonly stage: GuiStage, _icons: ItemIcons, private readonly inv: Inventory) {
    stage.interactive = false;
    this.root.style.cssText = 'position:absolute;left:0;top:0;width:100%;height:100%;pointer-events:none;';
    stage.content.appendChild(this.root);

    // full-screen effects live outside the scaled content so they cover the whole viewport
    this.waterEl.style.cssText =
      'position:absolute;inset:0;display:none;pointer-events:none;background:rgba(20,60,140,0.35);' +
      'box-shadow:inset 0 0 120px rgba(0,20,80,0.6);';
    this.vignetteEl.style.cssText =
      'position:absolute;inset:0;opacity:0;pointer-events:none;' +
      'background:radial-gradient(ellipse at center,rgba(255,0,0,0) 45%,rgba(200,0,0,0.55) 100%);';
    stage.el.insertBefore(this.waterEl, stage.content);
    stage.el.appendChild(this.vignetteEl);

    this.hotbarBg = new Spr('hotbar', 0, 0, 182, 22);
    this.selection = new Spr('hotbar_selection', 0, 0, 24, 23);
    this.offhandBg = new Spr('hotbar_offhand_left', 0, 0, 29, 24);
    this.offhandBg.el.style.display = 'none';
    this.root.append(this.hotbarBg.el, this.offhandBg.el);
    for (let i = 0; i < HOTBAR; i++) {
      const s = document.createElement('div');
      s.style.cssText = 'position:absolute;width:16px;height:16px;pointer-events:none;';
      this.slotEls.push(s);
      this.root.appendChild(s);
    }
    this.offhandEl.style.cssText = 'position:absolute;width:16px;height:16px;pointer-events:none;';
    this.root.append(this.offhandEl, this.selection.el);

    for (let i = 0; i < 10; i++) {
      this.heartBg.push(new Spr('heart/container', 0, 0, 9, 9));
      this.heartBlink.push(new Spr('', 0, 0, 9, 9));
      this.heartFg.push(new Spr('', 0, 0, 9, 9));
      this.foodBg.push(new Spr('food_empty', 0, 0, 9, 9));
      this.foodFg.push(new Spr('', 0, 0, 9, 9));
      this.armorSp.push(new Spr('', 0, 0, 9, 9));
      this.airSp.push(new Spr('', 0, 0, 9, 9));
    }
    for (const group of [this.heartBg, this.heartBlink, this.heartFg, this.foodBg, this.foodFg, this.armorSp, this.airSp]) {
      for (const s of group) this.root.appendChild(s.el);
    }
    this.xpBg = new Spr('experience_bar_background', 0, 0, 182, 5);
    this.xpFg = new Spr('experience_bar_progress', 0, 0, 182, 5);
    this.xpFg.el.style.backgroundSize = '182px 5px';
    this.xpFg.el.style.width = '0px';
    this.root.append(this.xpBg.el, this.xpFg.el);

    // The 12x5 locator background is a stretchable frame — 1px dark border, green
    // gradient centre — so it goes through border-image; background-size would
    // scale the border along with the middle. Smooth filtering, like vanilla's
    // GUI blit (the bar is a soft gradient, not pixel art).
    this.locatorBg.style.cssText =
      'position:absolute;width:182px;height:5px;box-sizing:border-box;pointer-events:none;display:none;' +
      'border-style:solid;border-color:transparent;border-width:1px;';
    this.locatorBg.style.borderImageSource = urlOf('locator_bar_background');
    this.locatorBg.style.borderImageSlice = '1 fill';
    this.locatorBg.style.borderImageWidth = '1px';
    this.locatorBg.style.borderImageRepeat = 'stretch';
    this.root.appendChild(this.locatorBg);

    this.crosshair = new Spr('crosshair', 0, 0, 15, 15);
    this.crosshair.el.style.mixBlendMode = 'difference';
    this.atkBg = new Spr('crosshair_attack_indicator_background', 0, 0, 16, 4);
    this.atkFg = new Spr('crosshair_attack_indicator_progress', 0, 0, 16, 4);
    this.atkFg.el.style.backgroundSize = '16px 4px';
    this.atkBg.el.style.display = this.atkFg.el.style.display = 'none';
    this.root.append(this.crosshair.el, this.atkBg.el, this.atkFg.el);

    for (const el of [this.heldNameEl, this.toastEl, this.coordsEl, this.debugEl]) {
      el.style.cssText = 'position:absolute;pointer-events:none;';
      this.root.appendChild(el);
    }
    this.debugEl.style.display = 'none';
    this.heldNameEl.style.transition = this.toastEl.style.transition = 'none';

    this.layout();
    this.refreshHotbar();
  }

  // ------------------------------------------------------------------ API ---

  setVisible(b: boolean): void {
    this.visible = b;
    this.root.style.display = b ? '' : 'none';
    this.vignetteEl.style.display = b ? '' : 'none';
  }

  /** Re-read the 9 hotbar stacks + offhand (update() also does this on change). */
  refreshHotbar(): void {
    for (let i = 0; i < HOTBAR; i++) ItemIcons.decorate(this.slotEls[i], this.inv.slots[i]);
    ItemIcons.decorate(this.offhandEl, this.inv.offhand);
    const off = !isEmpty(this.inv.offhand);
    this.offhandBg.el.style.display = off ? '' : 'none';
    this.offhandEl.style.display = off ? '' : 'none';
    this.invSig = this.signature();
  }

  /** Flash the selected item's name above the hotbar (2 s, fading). */
  showHeldName(): void {
    const s = this.inv.held;
    this.heldNameEl.replaceChildren();
    this.heldNameUntil = 0;
    if (isEmpty(s)) { this.heldNameEl.style.display = 'none'; return; }
    const name = stackName(s) || itemDef(s.id).name;
    if (!name) { this.heldNameEl.style.display = 'none'; return; }
    const w = textWidth(name);
    const box = document.createElement('div');
    box.style.cssText = `position:absolute;left:-2px;top:-2px;width:${w + 4}px;height:12px;background:rgba(0,0,0,0.25);`;
    const t = mcText(name, { color: s.ench?.length && s.id !== I.ENCHANTED_BOOK ? '#55ffff' : '#ffffff', shadow: true });
    t.style.cssText = `${s.name ? 'transform:skewX(-12deg);transform-origin:50% 100%;' : ''}position:absolute;left:0;top:0;width:${t.width}px;height:${t.height}px;image-rendering:pixelated;`;
    this.heldNameEl.append(box, t);
    this.heldNameEl.dataset.w = String(w);
    this.heldNameEl.style.display = '';
    this.heldNameEl.style.opacity = '1';
    this.heldNameUntil = performance.now() + 2000;
    this.layoutHeldName();
  }

  /** Action-bar style status message above the hotbar (2.5 s). */
  toast(text: string): void {
    this.toastEl.replaceChildren();
    const w = textWidth(text);
    const box = document.createElement('div');
    box.style.cssText = `position:absolute;left:-2px;top:-2px;width:${w + 4}px;height:12px;background:rgba(0,0,0,0.25);`;
    const t = mcText(text, { color: '#ffffff', shadow: true });
    t.style.cssText = `position:absolute;left:0;top:0;width:${t.width}px;height:${t.height}px;image-rendering:pixelated;`;
    this.toastEl.append(box, t);
    this.toastEl.dataset.w = String(w);
    this.toastEl.style.display = '';
    this.toastEl.style.opacity = '1';
    this.toastUntil = performance.now() + 2500;
    this.layoutToast();
  }

  /** Top-left coordinates readout ('' hides it). Hidden while the debug overlay is up. */
  setCoords(t: string): void {
    if (t === this.coordsText) return;
    this.coordsText = t;
    this.paintLines(this.coordsEl, t ? t.split('\n') : [], false);
    this.coordsEl.style.display = t && !this.debugOn ? '' : 'none';
  }

  /** Vanilla F3-style text: white lines on translucent grey strips, top-left. */
  updateDebug(lines: string[]): void {
    const t = lines.join('\n');
    if (t === this.debugText) return;
    this.debugText = t;
    this.paintLines(this.debugEl, lines, true);
  }

  toggleDebug(): void {
    this.debugOn = !this.debugOn;
    this.debugEl.style.display = this.debugOn ? '' : 'none';
    this.coordsEl.style.display = !this.debugOn && this.coordsText ? '' : 'none';
  }

  setWater(on: boolean): void { this.waterEl.style.display = on ? 'block' : 'none'; }

  // ---------------------------------------------------------------- frame ---

  update(s: HudState): void {
    if (this.stage.width !== this.W || this.stage.height !== this.H) this.layout();
    if (!this.visible) return;
    const now = performance.now();

    // inventory edits / selection change (cheap signature compare)
    const sig = this.signature();
    if (sig !== this.invSig) this.refreshHotbar();
    const held = this.inv.held;
    const heldKey = `${this.inv.selected}:${isEmpty(held) ? 0 : held.id}`;
    if (heldKey !== this.lastHeldKey) {
      this.lastHeldKey = heldKey;
      this.showHeldName();
    }
    if (this.inv.selected !== this.lastSel) {
      this.lastSel = this.inv.selected;
      this.selection.pos(this.cx() - 91 - 1 + this.lastSel * 20, this.H - 22 - 1);
    }
    if (this.heldNameUntil) {
      const left = this.heldNameUntil - now;
      if (left <= 0) { this.heldNameEl.style.display = 'none'; this.heldNameUntil = 0; }
      else this.heldNameEl.style.opacity = String(Math.min(1, left / 500));
    }
    if (this.toastUntil) {
      const left = this.toastUntil - now;
      if (left <= 0) { this.toastEl.style.display = 'none'; this.toastUntil = 0; }
      else this.toastEl.style.opacity = String(Math.min(1, left / 500));
    }

    const cflag = s.creative ? '1' : '0';
    if (this.heldNameEl.dataset.c !== cflag) { this.heldNameEl.dataset.c = cflag; this.layoutHeldName(); }
    this.vignetteEl.style.opacity = String(Math.max(0, Math.min(1, s.hurtFlash)));

    const survival = !s.creative;
    this.updateHearts(s, survival);
    this.updateFood(s, survival);
    this.updateArmor(s, survival);
    this.updateAir(s, survival);
    // the locator bar stands in for the XP bar whenever there is one to draw
    this.updateXp(s, survival, !this.updateLocator(s));
    this.updateCrosshair(s);
  }

  // ------------------------------------------------------------- private ---

  private cx(): number { return Math.floor(this.W / 2); }

  private signature(): string {
    let h = '';
    for (let i = 0; i < HOTBAR; i++) {
      const s = this.inv.slots[i];
      h += `${s.id},${s.count},${s.dmg};`;
    }
    const o = this.inv.offhand;
    return `${h}${o.id},${o.count},${o.dmg}`;
  }

  /** Position every static piece for the current stage size. */
  private layout(): void {
    this.W = this.stage.width;
    this.H = this.stage.height;
    const cx = this.cx(), H = this.H;
    this.hotbarBg.pos(cx - 91, H - 22);
    this.offhandBg.pos(cx - 91 - 29, H - 23);
    this.offhandEl.style.left = cx - 91 - 26 + 'px';
    this.offhandEl.style.top = H - 16 - 3 + 'px';
    for (let i = 0; i < HOTBAR; i++) {
      this.slotEls[i].style.left = cx - 90 + i * 20 + 2 + 'px';
      this.slotEls[i].style.top = H - 16 - 3 + 'px';
    }
    this.selection.pos(cx - 91 - 1 + Math.max(0, this.inv.selected) * 20, H - 22 - 1);
    this.xpBg.pos(cx - 91, H - 29);
    this.xpFg.pos(cx - 91, H - 29);
    this.crosshair.pos(cx - 7, Math.floor(H / 2) - 7);
    this.atkBg.pos(cx - 8, Math.floor(H / 2) - 7 + 16);
    this.atkFg.pos(cx - 8, Math.floor(H / 2) - 7 + 16);
    for (let i = 0; i < 10; i++) {
      this.armorSp[i].pos(cx - 91 + i * 8, H - 49);
      this.foodBg[i].pos(cx + 91 - i * 8 - 9, H - 39);
      this.airSp[i].pos(cx + 91 - i * 8 - 9, H - 49);
    }
    this.coordsEl.style.left = this.debugEl.style.left = '2px';
    this.coordsEl.style.top = this.debugEl.style.top = '2px';
    this.layoutHeldName();
    this.layoutToast();
    this.xpLevelShown = -1;
    this.lastHealth = -2; // force heart reposition
  }

  private layoutHeldName(): void {
    const w = +(this.heldNameEl.dataset.w ?? 0);
    const creative = this.heldNameEl.dataset.c === '1';
    this.heldNameEl.style.left = this.cx() - Math.floor(w / 2) + 'px';
    this.heldNameEl.style.top = this.H - 59 + (creative ? 14 : 0) + 'px';
  }
  private layoutToast(): void {
    const w = +(this.toastEl.dataset.w ?? 0);
    this.toastEl.style.left = this.cx() - Math.floor(w / 2) + 'px';
    this.toastEl.style.top = this.H - 68 + 'px';
  }

  private paintLines(host: HTMLElement, lines: string[], stripBg: boolean): void {
    host.replaceChildren();
    lines.forEach((l, i) => {
      if (!l) return;
      const w = textWidth(l);
      if (stripBg) {
        const bg = document.createElement('div');
        bg.style.cssText = `position:absolute;left:-1px;top:${i * 9 - 1}px;width:${w + 2}px;height:9px;background:rgba(80,80,80,0.56);`;
        host.appendChild(bg);
      }
      const t = mcText(l, { color: '#e0e0e0', shadow: true });
      t.style.cssText = `position:absolute;left:0;top:${i * 9}px;width:${t.width}px;height:${t.height}px;image-rendering:pixelated;`;
      host.appendChild(t);
    });
  }

  private updateHearts(s: HudState, survival: boolean): void {
    const maxH = Math.max(2, Math.ceil(s.maxHealth / 2) * 2);
    const health = Math.max(0, Math.ceil(s.health));
    // damage blink: remember the pre-hit value for 20 ticks (vanilla invulnerableTime/2 flash)
    if (this.lastHealth === -1 || this.lastHealth === -2) {
      this.displayHealth = health;
    } else if (health < this.lastHealth) {
      this.blinkUntilTick = s.tick + 20;
      this.displayHealth = this.lastHealth;
    } else if (health > this.lastHealth) {
      this.displayHealth = health;
      this.blinkUntilTick = 0;
    }
    this.lastHealth = health;
    const blinking = s.tick < this.blinkUntilTick;
    if (!blinking) this.displayHealth = health;
    const blinkPhase = blinking && (((this.blinkUntilTick - s.tick) / 3) | 0) % 2 === 1;
    const lowShake = s.lowHealth || health <= 4;
    const cx = this.cx(), base = this.H - 39;
    const style = s.heartStyle ?? 'normal';
    const wave = regenHeart(s.tick, s.regen === true, s.maxHealth);
    for (let i = 0; i < 10; i++) {
      const show = survival && i < Math.min(10, maxH / 2);
      const bg = this.heartBg[i], fg = this.heartFg[i], bl = this.heartBlink[i];
      bg.el.style.display = fg.el.style.display = bl.el.style.display = show ? '' : 'none';
      if (!show) continue;
      const shake = lowShake ? Math.floor(rnd01(s.tick, i) * 2) : 0;
      const x = cx - 91 + i * 8, y = base + shake - (i === wave ? 2 : 0);
      bg.pos(x, y); fg.pos(x, y); bl.pos(x, y);
      bg.set(blinkPhase ? 'heart/container_blinking' : 'heart/container');
      const hp = health - i * 2;
      fg.set(heartSprite(style, hp));
      // vanilla: in the blink phase the pre-hit hearts are drawn in the white-outline variant,
      // the real (smaller) health is then drawn over them
      const dp = this.displayHealth - i * 2;
      bl.set(blinkPhase ? heartSprite(style, dp, true) : '');
    }
  }

  /** Mounted: the mount-heart row replaces the food bar and the jump bar replaces the XP bar (RideHud). */
  riding = false;

  private updateFood(s: HudState, survival: boolean): void {
    survival = survival && !this.riding;
    const food = Math.max(0, Math.min(20, Math.ceil(s.food)));
    const shake = s.saturation <= 0 && s.tick % (food * 3 + 1) === 0;
    const cx = this.cx(), base = this.H - 39;
    for (let i = 0; i < 10; i++) {
      const bg = this.foodBg[i], fg = this.foodFg[i];
      bg.el.style.display = fg.el.style.display = survival ? '' : 'none';
      if (!survival) continue;
      let y = base;
      if (shake) y += Math.floor(rnd01(s.tick, 20 + i) * 3) - 1;
      const x = cx + 91 - i * 8 - 9;
      bg.pos(x, y); fg.pos(x, y);
      const f = food - i * 2;
      bg.set(foodEmptySprite(s.hungry === true));
      fg.set(foodSprite(s.hungry === true, f));
    }
  }

  private updateArmor(s: HudState, survival: boolean): void {
    const a = Math.round(s.armor);
    const cx = this.cx();
    for (let i = 0; i < 10; i++) {
      const sp = this.armorSp[i];
      if (!survival || a <= 0) { sp.el.style.display = 'none'; continue; }
      sp.el.style.display = '';
      sp.pos(cx - 91 + i * 8, this.H - 49);
      const v = a - i * 2;
      sp.set(v >= 2 ? 'armor_full' : v === 1 ? 'armor_half' : 'armor_empty');
    }
  }

  private updateAir(s: HudState, survival: boolean): void {
    const submerged = survival && s.air < s.maxAir;
    // vanilla: bubbles appear once air < max; the row sits above food (and above armor row's x-range: right side)
    const full = Math.ceil(((s.air - 2) * 10) / s.maxAir);
    const partial = Math.ceil((s.air * 10) / s.maxAir) - full;
    const cx = this.cx();
    for (let i = 0; i < 10; i++) {
      const sp = this.airSp[i];
      if (!submerged) { sp.el.style.display = 'none'; continue; }
      sp.el.style.display = '';
      sp.pos(cx + 91 - i * 8 - 9, this.H - 49);
      sp.set(i < full ? 'air' : i < full + partial ? 'air_bursting' : 'air_empty');
    }
  }

  /**
   * Vanilla locator bar: same 182x5 rect as the XP bar, dots on the camera's
   * bearing, four sprite sizes by distance, an up/down pointer when the target
   * sits off the crosshair. Returns true when the bar took the XP bar's place.
   */
  private updateLocator(s: HudState): boolean {
    const list = s.locator ?? null;
    const left = this.cx() - 91, top = this.H - 29;
    this.locatorBg.style.display = list ? '' : 'none';
    if (!list) {
      for (const d of this.locatorDots) { d.dot.style.display = 'none'; d.arrow.style.display = 'none'; }
      return false;
    }
    this.locatorBg.style.left = left + 'px';
    this.locatorBg.style.top = top + 'px';
    let n = 0;
    for (const w of list) {
      if (n >= LOCATOR_MAX) break;
      // vanilla draws the forward 120 degrees only: what is behind you is not
      // on the bar at all (it reappears as you turn)
      if (!(Math.abs(w.bearing) <= LOCATOR_ARC)) continue;
      const d = this.locatorDot(n++);
      const center = Math.max(left + 4, Math.min(left + 178, left + 91 + (w.bearing / LOCATOR_ARC) * 91));
      const x = Math.round(center) - 4, y = top - 2; // the 9x9 dot is centred on the 5px bar
      const band = Math.max(0, Math.min(LOCATOR_STEPS - 1,
        Math.floor((w.dist - LOCATOR_NEAR) / ((LOCATOR_FAR - LOCATOR_NEAR) / LOCATOR_STEPS))));
      const sprite = `locator_bar_dot/default_${band}`;
      const color = locatorColor(w.id);
      d.dot.style.display = '';
      d.dot.style.left = x + 'px';
      d.dot.style.top = y + 'px';
      if (d.dotSprite !== sprite || d.color !== color) {
        tintSprite(d.dot, sprite, color, 9, 9);
        d.dotSprite = sprite; d.color = color;
      }
      const up = w.elev > 0;
      d.arrow.style.display = w.elev === 0 ? 'none' : '';
      if (w.elev !== 0) {
        const arrow = up ? 'locator_bar_arrow_up' : 'locator_bar_arrow_down';
        d.arrow.style.left = (x + 1) + 'px';
        d.arrow.style.top = (up ? y - 5 : y + 9) + 'px';
        if (d.arrowSprite !== arrow || d.color !== color) {
          // the sprites hold two 7x5 frames; the first is the pointer
          tintSprite(d.arrow, arrow, color, 7, 5);
          d.arrowSprite = arrow;
        }
      }
    }
    for (let i = n; i < this.locatorDots.length; i++) {
      this.locatorDots[i].dot.style.display = 'none';
      this.locatorDots[i].arrow.style.display = 'none';
    }
    return true;
  }

  /** One pooled dot + its vertical pointer, built on first use. */
  private locatorDot(i: number): LocatorDot {
    const have = this.locatorDots[i];
    if (have) return have;
    const box = (w: number, h: number): HTMLDivElement => {
      const el = document.createElement('div');
      el.style.cssText = `position:absolute;width:${w}px;height:${h}px;display:none;${PIX}`;
      this.root.appendChild(el);
      return el;
    };
    const d: LocatorDot = { dot: box(9, 9), arrow: box(7, 5), dotSprite: '', arrowSprite: '', color: '' };
    this.locatorDots[i] = d;
    return d;
  }

  private updateXp(s: HudState, survival: boolean, show = true): void {
    const vis = survival && show && !this.riding;
    this.xpBg.el.style.display = this.xpFg.el.style.display = vis ? '' : 'none';
    if (this.xpLevelEl) this.xpLevelEl.style.display = vis && s.xpLevel > 0 ? '' : 'none';
    if (!vis) return;
    const w = Math.max(0, Math.min(182, Math.floor(s.xpProgress * 183)));
    this.xpFg.el.style.width = w + 'px';
    this.xpFg.el.style.backgroundSize = '182px 5px';
    if (s.xpLevel > 0 && s.xpLevel !== this.xpLevelShown) {
      this.xpLevelShown = s.xpLevel;
      this.xpLevelEl?.remove();
      const txt = String(s.xpLevel);
      const tw = textWidth(txt);
      const el = document.createElement('div');
      const x = this.cx() - Math.floor(tw / 2), y = this.H - 35;
      el.style.cssText = `position:absolute;left:${x}px;top:${y}px;width:${tw}px;height:8px;pointer-events:none;`;
      // vanilla draws the number in black four times (outline) then green on top
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
        const o = mcText(txt, { color: '#000000' });
        o.style.cssText = `position:absolute;left:${dx}px;top:${dy}px;width:${o.width}px;height:${o.height}px;image-rendering:pixelated;`;
        el.appendChild(o);
      }
      const g = mcText(txt, { color: '#80ff20' });
      g.style.cssText = `position:absolute;left:0;top:0;width:${g.width}px;height:${g.height}px;image-rendering:pixelated;`;
      el.appendChild(g);
      this.root.appendChild(el);
      this.xpLevelEl = el;
    }
  }

  private updateCrosshair(s: HudState): void {
    const f = Math.max(0, Math.min(1, s.attackCooldown));
    const showBar = f < 1 && !s.creative && (COMBAT_EDITION !== 'bedrock' || s.showCooldownBar === true);
    this.atkBg.el.style.display = this.atkFg.el.style.display = showBar ? '' : 'none';
    if (showBar) this.atkFg.el.style.width = Math.floor(f * 17) + 'px';
  }
}
